/**
 * La malla P2P del share multi-espectador (spec §14, decisión 2026-09-09): UN sharer mantiene UN
 * RTCPeerConnection POR viewer y le manda la MISMA captura a cada uno. Esta es la parte PURA —
 * decide a quién armarle un peer, a quién re-apuntar (cambió de pestaña) y a quién cerrar, y reparte
 * el presupuesto de subida entre los peers vivos — sin tocar el DOM ni RTCPeerConnection, así corre
 * en vitest/node (`mesh.test.ts`).
 *
 * Reglas del contrato:
 *  - Cap de espectadores simultáneos (`MAX_VIEWERS`, configurable por el broadcast). Es un límite
 *    FÍSICO de subida, no una preferencia: N peers = N× subida y N× codificadores (sin simulcast).
 *  - El bitrate por viewer BAJA al crecer el número: presupuesto de subida repartido entre los
 *    peers, con piso (`MIN_BITRATE_KBPS`) y techo (el `maxBitrateKbps` del broadcast, o 2,5 Mbps).
 *  - Cuando la subida se estresa (algún peer limitado por banda, o el reparto toca el piso, o se
 *    llegó al cap) se AVISA al sharer: no se esconde.
 *  - Compat 1:1: con UN solo viewer el reparto da el techo entero y todo se comporta como antes.
 */
import type { BroadcastDTO, ParticipantDTO, PeerStatsLike } from './types';
import type { PeerPhase } from './peer';

/** Tope de espectadores simultáneos por broadcast (v1 malla P2P; 15+ pediría un SFU). */
export const MAX_VIEWERS = 6;
/** Presupuesto TOTAL de subida a repartir (kbps) si el broadcast no trae uno. */
export const DEFAULT_UPLOAD_BUDGET_KBPS = 6_000;
/** Piso por viewer: por debajo el texto ya no se lee, así que se avisa en vez de seguir bajando. */
export const MIN_BITRATE_KBPS = 300;
/** Techo por viewer si el broadcast no fija otro: libwebrtc capa a ~2,5 Mbps para pantalla. */
export const CEILING_BITRATE_KBPS = 2_500;

/** Estados de participante que exigen un peer vivo del lado del sharer (aceptó o ya conecta/mira). */
const NEEDS_PEER = new Set(['accepted', 'live']);

/** Participantes a los que el sharer les mantiene un peer (aceptados/vivos con pestaña conocida). */
export function activeParticipants(b: BroadcastDTO): ParticipantDTO[] {
  return b.participants.filter((p) => NEEDS_PEER.has(p.status) && !!p.viewerTabId);
}

/** Cuántos están efectivamente MIRANDO (peer en 'live'): para "N watching" y el aviso de estrés. */
export function liveCount(b: BroadcastDTO): number {
  return b.participants.filter((p) => p.status === 'live').length;
}

/** ¿Se puede invitar a más? (cuenta invitados + conectados contra el cap del broadcast). */
export function occupancy(b: BroadcastDTO): { used: number; max: number; full: boolean } {
  const used = b.participants.filter((p) => p.status === 'pending' || p.status === 'accepted' || p.status === 'live').length;
  const max = b.maxViewers > 0 ? b.maxViewers : MAX_VIEWERS;
  return { used, max, full: used >= max };
}

/**
 * Reparto del presupuesto de subida entre los peers. Es un límite físico real: `budget / nPeers`,
 * recortado entre el piso y el techo. Con un solo peer da el techo entero (compat 1:1).
 */
export function perViewerBitrateKbps(opts: { uploadBudgetKbps: number; nPeers: number; ceilingKbps: number | null }): number {
  const budget = opts.uploadBudgetKbps > 0 ? opts.uploadBudgetKbps : DEFAULT_UPLOAD_BUDGET_KBPS;
  const ceiling = opts.ceilingKbps && opts.ceilingKbps > 0 ? opts.ceilingKbps : CEILING_BITRATE_KBPS;
  const n = Math.max(1, Math.floor(opts.nPeers));
  const share = Math.floor(budget / n);
  return Math.max(MIN_BITRATE_KBPS, Math.min(ceiling, share));
}

/** Lo mínimo que necesita saber la reconciliación de cada peer que ya existe. */
export type ExistingPeer = { viewerId: string; peerTabId: string | null };

/**
 * Plan de reconciliación de la malla contra el roster del broadcast:
 *  - `add`: participantes activos sin peer → armar uno nuevo (invitar en vivo NO corta a los demás).
 *  - `retarget`: el viewer sigue pero se movió de pestaña/ventana (cambió `viewerTabId`) → re-apuntar.
 *  - `close`: peers cuyo participante ya no está activo (salió, lo echaron, venció) → cerrar.
 *  - `bitrateKbps`: el cap por viewer que hay que aplicar a CADA peer con el número de ahora.
 */
export type ReconcilePlan = {
  add: ParticipantDTO[];
  retarget: ParticipantDTO[];
  close: string[];
  bitrateKbps: number;
};

export function planReconcile(b: BroadcastDTO, existing: readonly ExistingPeer[]): ReconcilePlan {
  const active = activeParticipants(b);
  const activeById = new Map(active.map((p) => [p.viewer.id, p] as const));
  const existingById = new Map(existing.map((e) => [e.viewerId, e] as const));
  const add: ParticipantDTO[] = [];
  const retarget: ParticipantDTO[] = [];
  for (const p of active) {
    const ex = existingById.get(p.viewer.id);
    if (!ex) add.push(p);
    else if (ex.peerTabId !== p.viewerTabId) retarget.push(p);
  }
  const close: string[] = [];
  for (const e of existing) if (!activeById.has(e.viewerId)) close.push(e.viewerId);
  const bitrateKbps = perViewerBitrateKbps({
    uploadBudgetKbps: b.uploadBudgetKbps,
    nPeers: active.length,
    ceilingKbps: b.maxBitrateKbps,
  });
  return { add, retarget, close, bitrateKbps };
}

/**
 * ¿La subida está estresada? (para el aviso "Quality reduced · N watching" y el aviso antes de
 * agregar a otro). Se dispara si algún peer está limitado por banda, si el reparto ya tocó el piso,
 * o si se llegó al cap de espectadores.
 */
export function uploadStressed(opts: { nPeers: number; perViewerKbps: number; anyBandwidthLimited: boolean; max: number }): boolean {
  return opts.anyBandwidthLimited || opts.perViewerKbps <= MIN_BITRATE_KBPS || opts.nPeers >= Math.max(1, opts.max);
}

/** ¿Algún peer reporta el codificador limitado por ancho de banda? (getStats de subida). */
export function anyBandwidthLimited(stats: Iterable<PeerStatsLike | null | undefined>): boolean {
  for (const s of stats) if (s && s.limitation === 'bandwidth') return true;
  return false;
}

/** Casilla del roster que ve el sharer: invitado, conectando, mirando o salió. */
export type RosterState = 'invited' | 'connecting' | 'watching' | 'left';

/**
 * Casilla del roster de un participante, combinando su estado de fila con la fase del peer: aceptó
 * pero el peer todavía no llegó a 'live' = "conectando"; 'live' con el peer 'live' = "mirando".
 */
export function rosterStateOf(p: ParticipantDTO, phase: PeerPhase | undefined): RosterState {
  switch (p.status) {
    case 'pending': return 'invited';
    case 'left': case 'declined': case 'expired': case 'removed': case 'failed': return 'left';
    case 'live': return phase === 'live' ? 'watching' : 'connecting';
    case 'accepted': default: return 'connecting';
  }
}

/** Orden estable del roster: mirando primero, después conectando, invitados y por último los que salieron. */
export function rosterRank(state: RosterState): number {
  return state === 'watching' ? 0 : state === 'connecting' ? 1 : state === 'invited' ? 2 : 3;
}
